import { TabContext, TabPanel } from '@mui/lab';
import { Tab, Tabs } from '@mui/material';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import AdminAccountTable from './AdminAccountTable';
import CompanyRequestsTable from './AdminCompanyTable';
import AdminIntelligenceRequests from './AdminIntelligenceRequests';
import AdminReviewStats from './AdminReviewStats';
import AdminPipelinesTable from './AdminPipelinesTable';
import AdminReleaseNotes from './AdminReleaseNotes';
import AdminUserTable from './AdminUserTable';

const TableContainer = styled.div`
  width: 100%;
  align-items: center;
  margin: auto;
`;

const BarWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  background-color: #f9f9f9;
  border-bottom: 1px solid #ddd;
`;

function AdminTabs() {
  const [tabValue, setTabValue] = useState('1');
  const [searchParams] = useSearchParams();
  const dealStagesEnabled = useFeatureFlagEnabled('deal-stage');

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && tabParam !== tabValue) {
      setTabValue(tabParam);
    }
  }, [searchParams]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  };

  const selectOptions = useMemo(
    () => [
      { name: 'Users', value: '1' },
      { name: 'Companies', value: '2' },
      { name: 'Review Stats', value: '3' },
      { name: 'Intelligence Requests', value: '4' },
      { name: 'Accounts', value: '5' },
      ...(dealStagesEnabled ? [{ name: 'Pipelines', value: '6' }] : []),
      { name: 'Release Notes', value: '7' },
    ],
    [dealStagesEnabled],
  );

  return (
    <TableContainer>
      <TabContext value={tabValue}>
        <BarWrapper>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTabs-flexContainer': {
                gap: 1,
              },
              '& .MuiTab-root': {
                minWidth: 120,
                fontWeight: 500,
                textTransform: 'none',
                fontSize: '0.875rem',
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#1976d2',
                height: 3,
              },
            }}
          >
            {selectOptions.map((option) => (
              <Tab key={option.value} label={option.name} value={option.value} />
            ))}
          </Tabs>
        </BarWrapper>

        <TabPanel value="1">
          <TableContainer>
            <AdminUserTable tabChange={tabValue} />
          </TableContainer>
        </TabPanel>
        <TabPanel value="2">
          <TableContainer>
            <CompanyRequestsTable tabChange="2" />
          </TableContainer>
        </TabPanel>
        <TabPanel value="3">
          <TableContainer>
            <AdminReviewStats />
          </TableContainer>
        </TabPanel>
        <TabPanel value="4">
          <AdminIntelligenceRequests />
        </TabPanel>
        <TabPanel value="5">
          <TableContainer>
            <AdminAccountTable />
          </TableContainer>
        </TabPanel>
        {dealStagesEnabled && (
          <TabPanel value="6">
            <TableContainer>
              <AdminPipelinesTable />
            </TableContainer>
          </TabPanel>
        )}
        <TabPanel value="7">
          <TableContainer>
            <AdminReleaseNotes />
          </TableContainer>
        </TabPanel>
      </TabContext>
    </TableContainer>
  );
}

export default AdminTabs;
